import type { AgencySummary, LoginResponse } from '@homecare/contracts';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { Button, Field, Notice } from '@homecare/ui';

import { request } from '../../lib/api';
import { useSession } from '../../lib/session';

import { AUTH_LINK, AuthFrame } from './auth-frame';
import { asksForCode, pickerNotice } from './sign-in-notices';

/** The selection token's life, mirrored from §7 so the countdown is honest. */
const SELECTION_TTL_SECONDS = 60;

/**
 * When the countdown is spoken rather than only shown.
 *
 * Not every second — a live region that fires sixty times would bury the screen
 * it is announcing. Twice is enough: once with time to read the list, once with
 * time to press a row.
 */
const ANNOUNCE_AT_SECONDS = [30, 10];

/**
 * The agency picker (§7, amendment A2).
 *
 * The password verified against more than one agency, so the person has to say
 * which one they are signing into.
 *
 * The agencies are **ledger rows**, not cards. A card grid says "pick a
 * product"; a ruled list says "this is the register you appear in", and a
 * register is what this actually is. They were briefly redrawn as bordered
 * cards and that was a mistake — the ruled treatment is the strongest thing on
 * any of these screens and it survives the split layout unchanged.
 *
 * The countdown is shown rather than hidden. The token lasts sixty seconds, and
 * a person who walks away and comes back deserves to be told why the screen
 * stopped working instead of discovering it by pressing a row.
 *
 * It is also **spoken**. A number ticking down in the corner is a warning only
 * to someone looking at the corner; a screen reader user reading the list top to
 * bottom would be dropped at zero with no notice at all. axe cannot catch that —
 * the markup is faultless, and the failure is entirely in the timing — so the
 * live region below is deliberate rather than a lint response.
 */
export function AgencyPicker({
  selectionToken,
  agencies,
  onStartOver,
}: {
  selectionToken: string;
  agencies: AgencySummary[];
  onStartOver: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const signIn = useSession((state) => state.signIn);

  const [failure, setFailure] = useState<unknown>(null);
  const [choosing, setChoosing] = useState<string | null>(null);
  // The agency whose account asked for its authenticator code. The selection
  // is still live while it is asked for — the API keeps it — so the countdown
  // keeps running and the code is sent against the same token.
  const [needsCode, setNeedsCode] = useState<AgencySummary | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [secondsLeft, setSecondsLeft] = useState(SELECTION_TTL_SECONDS);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setSecondsLeft((left) => Math.max(0, left - 1)), 1000);
    return () => clearInterval(timer);
  }, []);

  // The live region's text changes only at the moments worth interrupting for,
  // so the announcement is a warning rather than a metronome.
  useEffect(() => {
    if (ANNOUNCE_AT_SECONDS.includes(secondsLeft)) {
      setAnnouncement(`${secondsLeft} seconds left to choose an agency.`);
    } else if (secondsLeft === 0) {
      setAnnouncement('This list has expired. Sign in again to get a new one.');
    }
  }, [secondsLeft]);

  const expired = secondsLeft === 0;

  async function choose(agency: AgencySummary, mfaCode?: string): Promise<void> {
    setFailure(null);
    setChoosing(agency.id);

    try {
      const result = await request<LoginResponse>('/v1/auth/login/select', {
        method: 'POST',
        body: { selectionToken, agencyId: agency.id, ...(mfaCode ? { mfaCode } : {}) },
      });

      if (result.outcome !== 'authenticated') throw new Error('Unexpected selection response');

      // This screen only exists because there were two or more candidates, so
      // it is the one place that knows the answer. See `agencyCount` in
      // session.ts for why it is not read back from `/v1/auth/me`.
      await signIn(
        { accessToken: result.accessToken, refreshToken: result.refreshToken },
        { agencyCount: agencies.length },
      );
      await navigate({ to: '/' });
    } catch (error) {
      if (asksForCode(error)) {
        setNeedsCode(agency);
        return;
      }
      // Every other refusal has spent the selection, so the list is no longer
      // usable and is taken away rather than left to fail a second time.
      setFailure(error);
    } finally {
      setChoosing(null);
    }
  }

  const submitCode = (event: FormEvent): void => {
    event.preventDefault();
    if (!needsCode) return;
    if (!/^[0-9]{6}$/.test(code.trim())) {
      setCodeError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setCodeError(undefined);
    void choose(needsCode, code.trim());
  };

  const notice = failure ? pickerNotice(failure) : null;

  return (
    <AuthFrame
      title="Choose your agency"
      lede="You work at more than one. Pick the one you're signing into."
    >
      <div className="grid gap-4">
        {notice && (
          <Notice
            tone="critical"
            title={notice.title}
            action={<Button onClick={onStartOver}>Back to sign in</Button>}
          >
            <span data-next-step>{notice.nextStep}</span>
          </Notice>
        )}

        {/* Outside the branch below on purpose: a live region only announces
            changes to a region that was already in the document, so one that
            appears at the same moment as its message says nothing. */}
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>

        {notice ? null : expired ? (
          <div>
            <p className="text-md text-ink">
              This list expired after {SELECTION_TTL_SECONDS} seconds.
            </p>
            <p className="text-ink-2 mt-2 text-sm">Sign in again and it will come straight back.</p>
            <Button className="mt-5 w-full" onClick={onStartOver}>
              Sign in again
            </Button>
          </div>
        ) : needsCode ? (
          <form noValidate onSubmit={submitCode} className="grid gap-3">
            <div className="grid gap-1" role="status">
              <p className="text-ink text-md">
                {needsCode.displayName} asks for your authenticator code.
              </p>
              <p className="text-ink-2 text-sm">Enter the 6-digit code your app is showing.</p>
            </div>
            <Field
              label="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              value={code}
              error={codeError}
              onChange={(event) => setCode(event.target.value)}
            />
            <Button type="submit" className="w-full" pending={choosing !== null}>
              Finish signing in
            </Button>
            <p className="text-ink-3 text-xs" data-numeric>
              This sign-in expires in {String(secondsLeft).padStart(2, '0')}s
            </p>
            <p className="text-center">
              <button
                type="button"
                className={`${AUTH_LINK} text-sm`}
                onClick={() => {
                  setNeedsCode(null);
                  setCode('');
                  setCodeError(undefined);
                }}
              >
                Choose a different agency
              </button>
            </p>
          </form>
        ) : (
          <>
            {/* The ledger. A rule above the first row and under every row, so
                the list reads as a register rather than a stack of buttons.

                The button *is* the row — full width, and the padding is the row
                height, so the whole strip from the left rule to the right one is
                pressable. A name that is a link inside a row people read as a
                row is a target you have to aim at. */}
            <ul className="border-line border-t">
              {agencies.map((agency) => (
                <li key={agency.id}>
                  <button
                    type="button"
                    disabled={choosing !== null}
                    onClick={() => void choose(agency)}
                    // The label says what pressing it does, and to which agency.
                    // "Select" describes the mechanism; this describes the
                    // outcome, which is the thing the person came here for.
                    aria-label={`Sign in to ${agency.displayName}`}
                    aria-busy={choosing === agency.id ? true : undefined}
                    className="border-line hover:bg-surface-2 duration-fast ease-calm group flex w-full items-center justify-between gap-4 border-b px-1 py-4 text-left transition-colors disabled:opacity-60"
                  >
                    <span className="text-ink text-md min-w-0 truncate font-semibold">
                      {agency.displayName}
                    </span>
                    <span className="text-ink-3 group-hover:text-brand flex-none whitespace-nowrap text-xs font-medium">
                      {choosing === agency.id ? (
                        'Signing in…'
                      ) : (
                        <>
                          Sign in <span aria-hidden="true">→</span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <p className="text-ink-3 text-xs" data-numeric>
              This list expires in {String(secondsLeft).padStart(2, '0')}s
            </p>
          </>
        )}
      </div>
    </AuthFrame>
  );
}
